import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { Marker, Popup, Source, Layer } from 'react-map-gl';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { 
  MapPinIcon, 
  WifiIcon, 
  BoltIcon, 
  StarIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  PowerIcon
} from '@heroicons/react/24/outline';
import { getVenueMascot, getVenueTypeColor } from '../../utils/venueMascots';
import { getVenueIcon } from '../../utils/venueIconMapping';
import { useVenues } from '../../services/venueService'; // ⭐ NEW: React Query hook

// ... (Keep all your existing constants and helper functions)
const POPUP_MARGIN = 25;
const MARKER_OFFSET = 60; // Space above marker for popup
const AUTO_ZOOM_LEVEL = 16;
const ANIMATION_DURATION = 1000;
const COLLISION_PADDING = 60;
const MIN_POPUP_HEIGHT = 350;
const MAX_POPUP_HEIGHT_RATIO = 0.85;
const CENTER_PADDING = 100;

const calculateOptimalPopupPosition = (markerScreenPos, popupDimensions, viewportSize) => {
  const { x: markerX, y: markerY } = markerScreenPos;
  const { width: popupWidth, height: popupHeight } = popupDimensions;
  const { width: screenWidth, height: screenHeight } = viewportSize;

  const spaceTop = markerY - POPUP_MARGIN;
  const spaceBottom = screenHeight - markerY - POPUP_MARGIN;
  const spaceLeft = markerX - POPUP_MARGIN;
  const spaceRight = screenWidth - markerX - POPUP_MARGIN;

  let optimalAnchor = 'bottom';
  let needsMapAdjustment = false;
  let offsetX = 0, offsetY = 0;
  let needsCentering = false;

  const halfPopupWidth = popupWidth / 2;
  const popupLeft = markerX - halfPopupWidth;
  const popupRight = markerX + halfPopupWidth;

  if (popupLeft < POPUP_MARGIN || popupRight > screenWidth - POPUP_MARGIN) {
    needsMapAdjustment = true;
    needsCentering = true;
    const centerX = screenWidth / 2;
    offsetX = centerX - markerX;
  }

  if (spaceBottom >= popupHeight + COLLISION_PADDING + MARKER_OFFSET) {
    optimalAnchor = 'top';
  } else if (spaceTop >= popupHeight + COLLISION_PADDING + MARKER_OFFSET) {
    optimalAnchor = 'bottom';
  } else {
    needsMapAdjustment = true;
    needsCentering = true;
    optimalAnchor = spaceBottom > spaceTop ? 'top' : 'bottom';

    const centerY = screenHeight / 2;
    
    if (optimalAnchor === 'top') {
      offsetY = centerY - markerY - (popupHeight / 2) - CENTER_PADDING - MARKER_OFFSET;
    } else {
      offsetY = centerY - markerY + (popupHeight / 2) + CENTER_PADDING + MARKER_OFFSET;
    }
  }

  return {
    anchor: optimalAnchor,
    needsMapAdjustment,
    needsCentering,
    offsetX,
    offsetY,
    adjustedAnchor: optimalAnchor
  };
};

const calculateAutoFitMapCenter = (venueLat, venueLng, popupPosition, mapRef, currentZoom) => {
  if (!popupPosition.needsMapAdjustment || !mapRef.current) return null;

  const map = mapRef.current.getMap();
  const targetZoom = Math.max(AUTO_ZOOM_LEVEL, currentZoom);

  const markerPoint = map.project([venueLng, venueLat]);

  const adjustedPoint = {
    x: markerPoint.x + popupPosition.offsetX,
    y: markerPoint.y + popupPosition.offsetY
  };

  const newCenter = map.unproject(adjustedPoint);

  return {
    center: newCenter,
    zoom: targetZoom,
    needsCentering: popupPosition.needsCentering
  };
};

const calculateResponsivePopupSize = (screenSize, hasNearbyMosque = false) => {
  const { width, height } = screenSize;

  let baseWidth;
  if (width < 480) {
    baseWidth = Math.min(width * 0.85, 280);
  } else if (width < 768) {
    baseWidth = Math.min(width * 0.75, 320);
  } else if (width < 1024) {
    baseWidth = Math.min(width * 0.40, 380);
  } else if (width < 1440) {
    baseWidth = Math.min(width * 0.30, 420);
  } else {
    baseWidth = Math.min(width * 0.25, 450);
  }

  let baseHeight;
  if (width < 768) {
    baseHeight = hasNearbyMosque ? 
      Math.max(220, Math.min(height * 0.45, 320)) : 
      Math.max(200, Math.min(height * 0.38, 280));
  } else {
    baseHeight = hasNearbyMosque ? 
      Math.min(height * 0.38, 400) : 
      Math.min(height * 0.34, 360);
  }

  const finalWidth = Math.min(baseWidth, width * 0.85);
  const finalHeight = Math.min(baseHeight, height * 0.45);

  return {
    width: finalWidth,
    height: finalHeight,
    padding: width < 768 ? '12px' : '16px',
    borderRadius: width < 768 ? '8px' : '12px'
  };
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// ⭐ OPTIMIZED: Use React Query cached data
const findNearestMosque = (venue, allVenues) => {
  const venueLat = venue.lat;
  const venueLng = venue.lng;

  if (!venueLat || !venueLng) return null;

  const mosques = allVenues.filter(v =>
    (v.venue_category === 'vc_religion_spiritual' ||
     v.venue_type === 'vt_mosque' ||
     v.venue_category_display === 'Religion & Spiritual') &&
    v.lat && v.lng
  );

  let nearestMosque = null;
  let minDistance = Infinity;

  mosques.forEach(mosque => {
    const mosqueLat = mosque.lat;
    const mosqueLng = mosque.lng;

    const distance = calculateDistance(venueLat, venueLng, mosqueLat, mosqueLng);

    if (distance < minDistance) {
      minDistance = distance;
      nearestMosque = { ...mosque, distance: distance };
    }
  });

  return nearestMosque;
};

const shouldShowMosqueInfo = (venue) => {
  const category = (venue.venue_category_display || venue.venue_type_display || '').toLowerCase();
  return category.includes('bar') || 
         category.includes('beach') || 
         category.includes('street vendor') ||
         category.includes('cafe') ||
         category.includes('restaurant') ||
         category.includes('gym') ||
         category.includes('yoga') ||
         category.includes('club') ||
         venue.venue_category === 'vc_bar' ||
         venue.venue_category === 'vc_street_vendor' ||
         venue.venue_category === 'vc_cafe' ||
         venue.venue_category === 'vc_restaurant' ||
         venue.venue_category === 'vc_gym' ||
         venue.venue_category === 'vc_yoga_studio' ||
         venue.venue_category === 'vc_beach_club';
};

const calculateBearing = (lat1, lon1, lat2, lon2) => {
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - 
            Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360;
};

const getDirectionArrow = (bearing) => {
  const directions = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];
  const index = Math.round(bearing / 45) % 8;
  return directions[index];
};

const getWifiStrength = (ssid, speed, pubWifi) => {
  if (!ssid && pubWifi !== 1) return 0;
  if (speed && speed > 50) return 3;
  if (speed && speed > 20) return 2;
  return pubWifi === 1 ? 1 : 0;
};

const getMarkerSize = (zoom) => {
  if (zoom < 10) return Math.max(18, zoom * 2.2);
  if (zoom < 12) return Math.max(26, zoom * 3.2);
  if (zoom < 14) return Math.max(34, zoom * 3.8);
  return Math.min(52, zoom * 4.2);
};

const getMarkerIcon = (venue, selectedFilters) => {
  return getVenueIcon(venue);
};

const getPopupHeaderColor = (venue, selectedFilters) => {
  if (selectedFilters.venueCategory) {
    const filterCategories = Array.isArray(selectedFilters.venueCategory) 
      ? selectedFilters.venueCategory 
      : [selectedFilters.venueCategory];
    
    if (filterCategories.includes(venue.venue_category_display)) {
      return '#f5933d';
    }
  }
  
  if (selectedFilters.venueSubType) {
    const filterSubTypes = Array.isArray(selectedFilters.venueSubType) 
      ? selectedFilters.venueSubType 
      : [selectedFilters.venueSubType];
    
    if (filterSubTypes.includes(venue.venue_type_display)) {
      return '#ea580c';
    }
  }
  
  return '#D9641F';
};

// ⭐ OPTIMIZED MAP MARKER WITH REACT QUERY
const MapMarker = memo(({ 
  venue, 
  isSelected = false, 
  onClick,
  onVenueSelect,
  zoom,
  selectedFilters = {},
  allVenues = [], // ⭐ NOW OPTIONAL - will use React Query if not provided
  nearestMosque = null,
  showPath = false,
  mosqueRoute = null,
  mapRef,
  isEventsMode = false,
  venuesWithEvents = new Set(),
  venuesEventTypes = new Map(),
  eventsFilters = {},
  showWifiBadges = true,
  showBudgetBadges = false,
  showRatingsBadges = false,
  showTimeBadges = false,
  venuesEventTimes = new Map(),
  showPaymentBadges = false,
  venuesEventPrices = new Map()
}) => {
  const { t } = useTranslation();
  const [showPopup, setShowPopup] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [popupPosition, setPopupPosition] = useState(null);
  const [showMosqueDetails, setShowMosqueDetails] = useState(false);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // ⭐ NEW: Use React Query to get all venues if not provided
  const { data: cachedVenues = [] } = useVenues({}, { 
    enabled: allVenues.length === 0 
  });
  
  // ⭐ Use cached venues if allVenues not provided
  const venuesForCalculation = useMemo(() => 
    allVenues.length > 0 ? allVenues : cachedVenues,
    [allVenues, cachedVenues]
  );

  // ⭐ OPTIMIZED: Calculate nearest mosque from cached data
  const calculatedNearestMosque = useMemo(() => 
    nearestMosque || (shouldShowMosqueInfo(venue) ? findNearestMosque(venue, venuesForCalculation) : null),
    [venue, venuesForCalculation, nearestMosque]
  );

  const { lng, lat } = useMemo(() => ({
    lng: venue.lng,
    lat: venue.lat
  }), [venue.lng, venue.lat]);

  // ... (Keep all your existing useEffect hooks and handlers - they're already optimized)
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const popupDimensions = useMemo(() => 
    calculateResponsivePopupSize(screenSize, !!calculatedNearestMosque),
    [screenSize, calculatedNearestMosque]
  );

  useEffect(() => {
    if (isSelected && mapRef?.current && lat && lng) {
      const map = mapRef.current.getMap();

      const keepPopupInFocus = () => {
        const markerScreenPos = map.project([parseFloat(lng), parseFloat(lat)]);
        const position = calculateOptimalPopupPosition(
          markerScreenPos,
          popupDimensions,
          screenSize
        );
        setPopupPosition(position);

        if (position.needsMapAdjustment) {
          const autoFitParams = calculateAutoFitMapCenter(
            parseFloat(lat), 
            parseFloat(lng), 
            position, 
            mapRef,
            zoom || 11
          );

          if (autoFitParams) {
            map.flyTo({
              center: [autoFitParams.center.lng, autoFitParams.center.lat],
              zoom: autoFitParams.zoom,
              duration: 400,
              curve: 1.2,
              easing: t => t * (2 - t),
              essential: true,
              padding: {
                top: CENTER_PADDING,
                bottom: CENTER_PADDING,
                left: CENTER_PADDING,
                right: CENTER_PADDING
              }
            });
          }
        } else {
          const currentZoom = map.getZoom();
          if (currentZoom < AUTO_ZOOM_LEVEL - 1) {
            map.flyTo({
              center: [parseFloat(lng), parseFloat(lat)],
              zoom: AUTO_ZOOM_LEVEL,
              duration: 300,
              essential: true,
              padding: {
                top: CENTER_PADDING,
                bottom: CENTER_PADDING,
                left: CENTER_PADDING,
                right: CENTER_PADDING
              }
            });
          }
        }
      };

      keepPopupInFocus();

      const handleMapMove = () => {
        if (isSelected) {
          requestAnimationFrame(keepPopupInFocus);
        }
      };

      const handleMapClick = (e) => {
        if (onClick) {
          onClick(null);
        }
      };

      const handleMapContainerClick = (e) => {
        const popupElement = document.querySelector('.venue-popup.enhanced-auto-fit-popup');
        if (popupElement && !popupElement.contains(e.target)) {
          if (onClick) {
            onClick(null);
          }
        }
      };

      map.on('move', handleMapMove);
      map.on('click', handleMapClick);
      
      const mapContainer = map.getContainer();
      mapContainer.addEventListener('click', handleMapContainerClick);

      return () => {
        map.off('move', handleMapMove);
        map.off('click', handleMapClick);
        mapContainer.removeEventListener('click', handleMapContainerClick);
      };
    }
  }, [isSelected, lat, lng, popupDimensions, screenSize, zoom, mapRef, onClick]);

  const wifiStrength = useMemo(() => 0, []); /* field removed - no Neon equivalent yet for WiFi fields */

  const markerSize = useMemo(() => getMarkerSize(zoom || 11), [zoom]);
  const venueMascot = useMemo(() => getVenueMascot(venue), [venue]);
  const venueTypeColor = useMemo(() => getVenueTypeColor(venue), [venue]);
  const markerIcon = useMemo(() => getMarkerIcon(venue, selectedFilters), [venue, selectedFilters]);
  const popupHeaderColor = useMemo(() => getPopupHeaderColor(venue, selectedFilters), [venue, selectedFilters]);

  // WiFi/Budget/Noise badges — fields removed, no Neon equivalent yet
  const shouldShowWifiSpeed = false;
  const shouldShowNoWifi = false;
  const shouldShowBudget = false;

  // Rating display logic - Show when Rating toggle is ON
  const shouldShowRating = useMemo(() => {
    return showRatingsBadges && !isEventsMode && zoom >= 12 && venue.google_rating;
  }, [showRatingsBadges, isEventsMode, zoom, venue.google_rating]);

  // Get event type for current venue
  const venueEventType = useMemo(() => {
    if (!isEventsMode || !venuesEventTypes.size) return null;
    
    // Check multiple possible venue identifiers to match with events
    const venueIdentifiers = [
      venue._id,                    // Primary venue ID
      venue.Dubai_id,              // Dubai venue ID
      venue.name,          // Account name (matches events API)
      venue.venue_name,            // Alternative venue name
      venue.name,            // Alternative venue name field
      venue.name                   // Generic name field
    ].filter(Boolean);
    
    // Find the first matching identifier that has an event type
    for (const identifier of venueIdentifiers) {
      if (venuesEventTypes.has(identifier)) {
        return venuesEventTypes.get(identifier);
      }
    }
    
    return null;
  }, [isEventsMode, venue, venuesEventTypes]);

  // Get event time for current venue
  const venueEventTime = useMemo(() => {
    if (!isEventsMode || !venuesEventTimes.size) return null;
    
    // Check multiple possible venue identifiers to match with events
    const venueIdentifiers = [
      venue._id,                    // Primary venue ID
      venue.Dubai_id,              // Dubai venue ID
      venue.name,          // Account name (matches events API)
      venue.venue_name,            // Alternative venue name
      venue.name,            // Alternative venue name field
      venue.name                   // Generic name field
    ].filter(Boolean);
    
    // Find the first matching identifier that has an event time
    for (const identifier of venueIdentifiers) {
      if (venuesEventTimes.has(identifier)) {
        return venuesEventTimes.get(identifier);
      }
    }
    
    return null;
  }, [isEventsMode, venue, venuesEventTimes]);

  // Get event price for current venue
  const venueEventPrice = useMemo(() => {
    if (!isEventsMode || !venuesEventPrices.size) return null;
    
    // Check multiple possible venue identifiers to match with events
    const venueIdentifiers = [
      venue._id,                    // Primary venue ID
      venue.Dubai_id,              // Dubai venue ID
      venue.name,          // Account name (matches events API)
      venue.venue_name,            // Alternative venue name
      venue.name,            // Alternative venue name field
      venue.name                   // Generic name field
    ].filter(Boolean);
    
    // Find the first matching identifier that has an event price
    for (const identifier of venueIdentifiers) {
      if (venuesEventPrices.has(identifier)) {
        return venuesEventPrices.get(identifier);
      }
    }
    
    return null;
  }, [isEventsMode, venue, venuesEventPrices]);

  // Time display logic - Show when time toggle is ON and in events mode
  const shouldShowTime = useMemo(() => {
    return showTimeBadges && isEventsMode && zoom >= 12 && venueEventTime;
  }, [showTimeBadges, isEventsMode, zoom, venueEventTime]);

  // Payment display logic - Show when payment toggle is ON and in events mode
  const shouldShowPayment = useMemo(() => {
    return showPaymentBadges && isEventsMode && zoom >= 12 && venueEventPrice;
  }, [showPaymentBadges, isEventsMode, zoom, venueEventPrice]);

  // ✅ Get category config for selected category
  const categoryConfig = useMemo(() => {
    const CATEGORY_CONFIG = {
      'Wellness & Fitness': { icon: '💪', color: '#10b981' },
      'Music & Nightlife': { icon: '🎵', color: '#8b5cf6' },
      'Food & Social': { icon: '🍽️', color: '#f59e0b' },
      'Sports & Recreation': { icon: '⚽', color: '#3b82f6' },
      'Arts & Culture': { icon: '🎨', color: '#ec4899' },
      'Education & Workshops': { icon: '🛠️', color: '#06b6d4' },
      'Networking & Business': { icon: '🤝', color: '#6366f1' },
      'Entertainment': { icon: '🎭', color: '#ef4444' },
      'Family & Kids': { icon: '👨‍👩‍👧', color: '#f97316' },
      'Technology': { icon: '💻', color: '#14b8a6' },
      'Promos': { icon: '🎁', color: '#ef4444' }
    };
    
    return eventsFilters.selectedCategory && CATEGORY_CONFIG[eventsFilters.selectedCategory] 
      ? CATEGORY_CONFIG[eventsFilters.selectedCategory] 
      : { icon: '🎉', color: '#10b981' };
  }, [eventsFilters.selectedCategory]);

  // Events mode display logic - Show party popper icon when in events mode, zoomed in, and venue has events
  const shouldShowEventsIcon = useMemo(() => {
    if (!isEventsMode || zoom < 12) return false;
    
    // Check multiple possible venue identifiers to match with events
    const venueIdentifiers = [
      venue._id,                    // Primary venue ID
      venue.Dubai_id,              // Dubai venue ID
      venue.name,          // Account name (matches events API)
      venue.venue_name,            // Alternative venue name
      venue.name,            // Alternative venue name field
      venue.name                   // Generic name field
    ].filter(Boolean);
    
    // Check if any of the venue identifiers match venues with events
    const hasEvents = venueIdentifiers.some(identifier => venuesWithEvents.has(identifier));
    
    // Debug logging for venue-event matching
    if (isEventsMode && hasEvents) {
      console.log('🎉 Venue has events:', {
        venueName: venue.name || venue.name || venue.name,
        venueId: venue._id || venue.Dubai_id,
        matchedIdentifiers: venueIdentifiers.filter(id => venuesWithEvents.has(id))
      });
    }
    
    return hasEvents;
  }, [isEventsMode, zoom, venue, venuesWithEvents]);

  const mosqueDirection = useMemo(() => {
    if (!calculatedNearestMosque || !lat || !lng) return null;

    const mosqueLat = calculatedNearestMosque.lat;
    const mosqueLng = calculatedNearestMosque.lng;

    if (!mosqueLat || !mosqueLng) return null;

    const bearing = calculateBearing(parseFloat(lat), parseFloat(lng), parseFloat(mosqueLat), parseFloat(mosqueLng));
    const arrow = getDirectionArrow(bearing);
    const distance = calculateDistance(parseFloat(lat), parseFloat(lng), parseFloat(mosqueLat), parseFloat(mosqueLng));

    return { bearing, arrow, mosqueLat, mosqueLng, distance: distance.toFixed(2) };
  }, [calculatedNearestMosque, lat, lng]);

  // ... (Keep all remaining code - handlers, pathGeoJSON, markerStyle, etc.)
  
  const pathGeoJSON = useMemo(() => {
    if (!showPath || !mosqueRoute) return null;

    return {
      type: 'Feature',
      properties: {
        distance: mosqueRoute.distance,
        duration: mosqueRoute.duration
      },
      geometry: mosqueRoute.geometry
    };
  }, [showPath, mosqueRoute]);

  const markerStyle = useMemo(() => {
    const baseStyle = {
      width: `${markerSize}px`,
      height: `${markerSize}px`,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.2s ease-out',
      transform: `translateZ(0) ${isSelected ? 'scale(1.35)' : 'scale(1)'}`,
      zIndex: isSelected ? 1000 : 1,
      position: 'relative',
      overflow: 'visible',
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      perspective: '1000px'
    };

    const matchesCategory = selectedFilters.venueCategory && 
      (Array.isArray(selectedFilters.venueCategory) 
        ? selectedFilters.venueCategory.includes(venue.venue_category_display)
        : selectedFilters.venueCategory === venue.venue_category_display);

    const matchesSubType = selectedFilters.venueSubType && 
      (Array.isArray(selectedFilters.venueSubType)
        ? selectedFilters.venueSubType.includes(venue.venue_type_display)
        : selectedFilters.venueSubType === venue.venue_type_display);

    return baseStyle;
  }, [markerSize, isSelected, selectedFilters, venue.venue_category_display, venue.venue_type_display]);

  // ===== STREET VENDOR TRAJECTORY HANDLING =====
// STREET VENDOR TRAJECTORY HANDLING
const vendorTrajectoryGeoJSON = useMemo(() => {
  // Only process for street vendors with location history
  if (!venue.isStreetVendor || !venue.locationHistory || venue.locationHistory.length === 0) {
    return null;
  }

  // ✅ CHANGE THIS: Show complete trail (last 24 hours instead of 1 hour)
  const twentyFourHoursAgo = new Date(Date.now() - (24 * 60 * 60 * 1000)); // 24 hours
  const recentHistory = venue.locationHistory.filter(loc => 
    new Date(loc.timestamp) > twentyFourHoursAgo
  );

  if (recentHistory.length < 2) {
    return null;
  }

  return {
    type: 'Feature',
    properties: {
      vendor: venue.vendorName,
      points: recentHistory.length,
      // ✅ ADD: Store timestamps for gradient effect
      startTime: recentHistory[0].timestamp,
      endTime: recentHistory[recentHistory.length - 1].timestamp
    },
    geometry: {
      type: 'LineString',
      coordinates: recentHistory.map(loc => loc.coordinates)
    }
  };
}, [venue.isStreetVendor, venue.locationHistory]);

// ===== TRAJECTORY POINT MARKERS =====
const trajectoryPoints = useMemo(() => {
  if (!venue.isStreetVendor || !venue.locationHistory) return [];

  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
  return venue.locationHistory
    .filter(loc => new Date(loc.timestamp) >= oneHourAgo)
    .slice(0, -1);
}, [venue.isStreetVendor, venue.locationHistory]);

useEffect(() => {
  console.log('🎯 Popup visibility check:', { 
    isSelected, 
    venueId: venue._id || venue.Dubai_id,
    showPopup,
    isAnimating 
  });
  
  if (isSelected) {
    setIsAnimating(true);
    // ✅ CRITICAL FIX: Ensure popup shows immediately
    setShowPopup(true);
    
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 150);

    return () => clearTimeout(timer);
  } else {
    setShowPopup(false);
    setIsAnimating(false);
    setPopupPosition(null);
  }
}, [isSelected, venue._id, venue.Dubai_id]); // ✅ Added venue IDs as dependencies

// ✅ ADDITIONAL FIX: Update handleMarkerClick to ensure proper state updates
const handleMarkerClick = useCallback((e) => {
  // ✅ CRITICAL: Stop all event propagation
  e.originalEvent?.stopPropagation();
  e.originalEvent?.preventDefault();
  e.stopPropagation?.();
  
  // ✅ Also stop at DOM level
  if (e.originalEvent) {
    e.originalEvent.stopImmediatePropagation();
  }

  console.log('🎯 Marker clicked:', {
    venueId: venue._id || venue.Dubai_id,
    venueName: venue.name,
    currentIsSelected: isSelected
  });

  // ✅ CRITICAL: Always call onClick to update parent state
  if (onClick) {
    onClick(venue._id || venue.Dubai_id);
  }

  if (mapRef?.current && lat && lng) {
    const map = mapRef.current.getMap();
    const currentZoom = map.getZoom();
    
    let targetZoom;
    if (currentZoom < AUTO_ZOOM_LEVEL) {
      targetZoom = AUTO_ZOOM_LEVEL;
    } else if (currentZoom < 17) {
      targetZoom = currentZoom + 1;
    } else {
      targetZoom = currentZoom;
    }

    targetZoom = Math.max(targetZoom, currentZoom);

    map.flyTo({
      center: [parseFloat(lng), parseFloat(lat)],
      zoom: targetZoom,
      duration: ANIMATION_DURATION,
      curve: 1.42,
      easing: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
      essential: true,
      padding: {
        top: 50,
        bottom: 200,
        left: 50,
        right: 50
      }
    });
  }
}, [onClick, venue._id, venue.Dubai_id, venue.name, lat, lng, mapRef, isSelected]);

  const handleViewDetails = useCallback(() => {
    if (onVenueSelect) {
      onVenueSelect(venue._id || venue.Dubai_id);
    }
  }, [onVenueSelect, venue._id, venue.Dubai_id]);

  const handleClosePopup = useCallback(() => {
    if (onClick) {
      onClick(null);
    }
  }, [onClick]);

  const handlePopupClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handlePopupContentClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleCloseButtonClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClick) {
      onClick(null);
    }
  }, [onClick]);

  const hasFilterMatch = useMemo(() => {
    const matchesCategory = selectedFilters.venueCategory && 
      (Array.isArray(selectedFilters.venueCategory) 
        ? selectedFilters.venueCategory.includes(venue.venue_category_display)
        : selectedFilters.venueCategory === venue.venue_category_display);

    const matchesSubType = selectedFilters.venueSubType && 
      (Array.isArray(selectedFilters.venueSubType)
        ? selectedFilters.venueSubType.includes(venue.venue_type_display)
        : selectedFilters.venueSubType === venue.venue_type_display);

    return matchesCategory || matchesSubType;
  }, [selectedFilters, venue.venue_category_display, venue.venue_type_display]);

  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);
  const isTablet = useMemo(() => screenSize.width >= 768 && screenSize.width < 1024, [screenSize.width]);

useEffect(() => {
  if (isSelected) {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setShowPopup(true);  // ✅ THIS SHOWS THE POPUP
      setIsAnimating(false);
    }, 150);

    return () => clearTimeout(timer);
  } else {
    setShowPopup(false);
    setIsAnimating(false);
    setPopupPosition(null);
  }
}, [isSelected]);

  if (!lat || !lng || isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) {
    return null;
  }

  const numLat = parseFloat(lat);
  const numLng = parseFloat(lng);

  return (
    <>  
        {vendorTrajectoryGeoJSON && isSelected && (
          <Source
            id={`vendor-trajectory-${venue.id}`}
            type="geojson"
            data={vendorTrajectoryGeoJSON}
          >
            {/* ✅ ENHANCED: Thicker, more visible path line */}
            <Layer
              id={`vendor-path-line-${venue.id}`}
              type="line"
              paint={{
                'line-color': [
                  'interpolate',
                  ['linear'],
                  ['line-progress'],
                  0, venue.isOperational ? '#86efac' : '#fca5a5', // Start color (lighter)
                  1, venue.isOperational ? '#10b981' : '#ef4444'  // End color (darker)
                ],
                'line-width': [
                  'interpolate',
                  ['exponential', 1.5],
                  ['zoom'],
                  10, 4,    // ✅ INCREASED from 3
                  15, 8,    // ✅ INCREASED from 5
                  18, 12    // ✅ INCREASED from 7
                ],
                'line-opacity': 0.9,  // ✅ INCREASED from 0.8
                'line-gradient': true
              }}
              layout={{
                'line-cap': 'round',
                'line-join': 'round'
              }}
            />

            {/* ✅ ENHANCED: Brighter glow for better visibility */}
            <Layer
              id={`vendor-path-glow-${venue.id}`}
              type="line"
              paint={{
                'line-color': venue.isOperational ? '#10b981' : '#ef4444',
                'line-width': [
                  'interpolate',
                  ['exponential', 1.5],
                  ['zoom'],
                  10, 10,   // ✅ INCREASED from 6
                  15, 16,   // ✅ INCREASED from 10
                  18, 22    // ✅ INCREASED from 14
                ],
                'line-opacity': 0.4,  // ✅ INCREASED from 0.3
                'line-blur': 6        // ✅ INCREASED from 4
              }}
              layout={{
                'line-cap': 'round',
                'line-join': 'round'
              }}
            />

            {/* ✅ DOTS ALONG PATH - More visible */}
            <Layer
              id={`vendor-path-dots-${venue.id}`}
              type="circle"
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  10, 5,    // ✅ INCREASED from 3
                  15, 8,    // ✅ INCREASED from 5
                  18, 12    // ✅ INCREASED from 7
                ],
                'circle-color': venue.isOperational ? '#10b981' : '#ef4444',
                'circle-opacity': 0.8,  // ✅ INCREASED from 0.7
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff'
              }}
            />
          </Source>
        )}

      {/* ===== TRAJECTORY POINT MARKERS ===== */}
      {trajectoryPoints.map((point, index) => (
        <motion.div key={`trajectory-${venue._id}-${index}`}>
          <Marker
            longitude={point.coordinates[0]}
            latitude={point.coordinates[1]}
            anchor="center"
            style={{ pointerEvents: 'none' }}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.7 }}
              exit={{ scale: 0, opacity: 0 }}
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: venue.isOperational ? '#10b981' : '#ef4444',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: `0 0 8px ${venue.isOperational ? '#10b981' : '#ef4444'}`,
                zIndex: 50
              }}
            />
          </Marker>
        </motion.div>
      ))}

      {/* ===== EXISTING VENDOR MARKER ===== */}
      <Marker
        longitude={numLng}
        latitude={numLat}
        anchor="bottom"
        onClick={handleMarkerClick}
      >
        <motion.div
          data-marker="true" // ✅ ADD THIS
          style={markerStyle}
          whileHover={{ 
            scale: isSelected ? 1.45 : 1.25,
            transition: { duration: 0.2, ease: "easeOut" }
          }}
          whileTap={{ 
            scale: isSelected ? 1.25 : 1.05,
            transition: { duration: 0.1 }
          }}
          initial={{ scale: 0, opacity: 0, y: 20 }}
          animate={{ 
            scale: isSelected ? 1.35 : 1,
            opacity: 1,
            y: 0,
            transition: { 
              type: "spring", 
              stiffness: 300, 
              damping: 25,
              mass: 0.8
            }
          }}
        >
          <img 
            src={markerIcon}
            alt={venue.venue_category_display || venue.venue_type_display || t('venue.venueFallback')}
            style={{
              width: `${markerSize * 1.0}px`,
              height: `${markerSize * 1.0}px`,
              objectFit: 'contain',
            }}
          />
          
          {/* WiFi Speed Badge - Shows when zoomed in */}
           <AnimatePresence>
              {(zoom >= 14 || isSelected) && (
               <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    bottom: `${markerSize - 10}px`, // Changed from +4 to -2 to move closer
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: '#f5933d', // Simplified - same color for both states
                    padding: isSelected ? '6px 10px' : '4px 8px',
                    borderRadius: '8px',
                    fontSize: isSelected ? '12px' : '11px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    // textShadow removed completely
                    zIndex: isSelected ? 15 : 10,
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {venue.name || venue.vendorName || venue.name || t('map.unnamedVenue')}
                </motion.div>
              )}

    
            {/* Real-time update indicator */}
          {venue.lastLocationUpdate && venue.isStreetVendor && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '16px',
                height: '16px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.5)',
                zIndex: 2
              }}
            >
              <motion.div
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  position: 'absolute',
                  inset: '-4px',
                  backgroundColor: '#10b981',
                  borderRadius: '50%',
                  opacity: 0.5
                }}
              />
            </motion.div>
          )}

  {shouldShowWifiSpeed && (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={{ scale: 1.1 }}
      style={{
        position: 'absolute',
        top: '50%',
        right: '-35px',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '4px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        zIndex: 1,
        cursor: 'pointer',
        minWidth: '45px'
      }}
      title={t('map.wifiSpeedTitle', { speed: venue.DL_SPeed_MBPS })}
    >
      <WifiIcon style={{
        width: '16px',
        height: '16px',
        color: '#10b981'
      }} />
      <span style={{
        fontSize: '10px',
        fontWeight: '600',
        color: '#374151',
        lineHeight: '1'
      }}>
        ↓{venue.DL_SPeed_MBPS} Mbps
      </span>
    </motion.div>
  )}
  
  {/* Budget Badge - Shows when Budget toggle is ON */}
  {shouldShowBudget && (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={{ scale: 1.1 }}
      style={{
        position: 'absolute',
        top: '50%',
        left: '-30px',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '4px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        zIndex: 1,
        cursor: 'pointer',
        minWidth: '35px'
      }}
      title={t('map.budgetTitle', { budget: venue.Budget_Friendly || venue.priceRange || t('common.na') })}
    >
      <span style={{
        fontSize: '12px'
      }}>💰</span>
      <span style={{
        fontSize: '10px',
        fontWeight: '600',
        color: '#374151',
        lineHeight: '1'
      }}>
        {venue.Budget_Friendly}
      </span>
    </motion.div>
  )}
  
  {/* Rating Badge - Shows when Rating toggle is ON */}
  {shouldShowRating && venue.Rating && (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={{ scale: 1.1 }}
      style={{
        position: 'absolute',
        top: '50%',
        left: '-30px',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '4px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        zIndex: 1,
        cursor: 'pointer',
        minWidth: '35px'
      }}
      title={t('map.ratingTitle', { rating: venue.Rating })}
    >
      <span style={{
        fontSize: '12px'
      }}>⭐</span>
      <span style={{
        fontSize: '10px',
        fontWeight: '600',
        color: '#374151',
        lineHeight: '1'
      }}>
        {venue.Rating.toFixed(1)}
      </span>
    </motion.div>
  )}
  
  {/* Operational Status Badge - For Street Vendors */}
  {venue.isStreetVendor && (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={{ scale: 1.1 }}
      style={{
        position: 'absolute',
        top: '50%',
        right: '-35px',
        transform: 'translateY(-50%)',
        backgroundColor: venue.isOperational ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        border: `1px solid ${venue.isOperational ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
        borderRadius: '8px',
        padding: '4px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        zIndex: 1,
        cursor: 'pointer',
        minWidth: '55px'
      }}
      title={venue.isOperational ? t('map.currentlyOpen') : t('map.currentlyClosed')}
    >
      <span style={{
        fontSize: '12px',
        lineHeight: '1'
      }}>
        {venue.isOperational ? '🟢' : '🔴'}
      </span>
      <span style={{
        fontSize: '10px',
        fontWeight: '600',
        color: venue.isOperational ? '#10b981' : '#ef4444',
        lineHeight: '1'
      }}>
        {venue.isOperational ? t('vs.open') : t('vs.closed')}
      </span>
    </motion.div>
  )}
  
  {/* Cuisine Tags Badge - For Street Vendors */}
  {venue.isStreetVendor && venue.cuisineTags && (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={{ scale: 1.1 }}
      style={{
        position: 'absolute',
        top: '50%',
        left: '-30px',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '4px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        zIndex: 1,
        cursor: 'pointer',
        minWidth: '50px'
      }}
      title={t('map.cuisineTitle', { cuisine: venue.cuisineTags })}
    >
      <span style={{
        fontSize: '12px'
      }}>🍜</span>
      <span style={{
        fontSize: '10px',
        fontWeight: '600',
        color: '#374151',
        lineHeight: '1',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '40px'
      }}>
        {venue.cuisineTags.split(';')[0]}
      </span>
    </motion.div>
  )}
  
  {/* Events Icon Badge - Shows when in events mode */}
  {shouldShowEventsIcon && (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={{ scale: 1.1 }}
      style={{
        position: 'absolute',
        top: '50%',
        right: '-35px',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '4px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        zIndex: 1,
        cursor: 'pointer',
        minWidth: '35px',
        whiteSpace: 'nowrap'
      }}
      title={t('map.eventsAvailable')}
    >
      <span style={{
        fontSize: '14px',
        lineHeight: '1'
      }}>
        {categoryConfig?.icon || '🎉'}
      </span>
      <span style={{
        fontSize: '12px',
        fontWeight: '600',
        color: '#374151',
        lineHeight: '1'
      }}>
        {venueEventType || eventsFilters?.selectedCategory || t('venue.events')}
      </span>
      
      {/* Show time in same badge when time toggle is ON */}
      {shouldShowTime && venueEventTime && (
        <>
          <span style={{
            fontSize: '12px',
            color: '#6b7280',
            lineHeight: '1'
          }}>
            •
          </span>
          <span style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
            lineHeight: '1'
          }}>
            {(() => {
              if (!venueEventTime) return '';
              
              const startTimePart = venueEventTime.startTime?.split('T')[1];
              if (!startTimePart) return '';
              const startTime = startTimePart.split(':').slice(0, 2).join(':');
              
              const endTimePart = venueEventTime.endTime?.split('T')[1];
              if (endTimePart) {
                const endTime = endTimePart.split(':').slice(0, 2).join(':');
                return `${startTime} - ${endTime}`;
              }
              
              return startTime;
            })()}
          </span>
        </>
      )}
      
      {/* Show payment in same badge when payment toggle is ON */}
      {shouldShowPayment && venueEventPrice && (
        <>
          <span style={{
            fontSize: '12px',
            color: '#6b7280',
            lineHeight: '1'
          }}>
            •
          </span>
          <span style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
            lineHeight: '1'
          }}>
            {venueEventPrice?.price} {venueEventPrice?.currency}
          </span>
        </>
      )}
    </motion.div>
  )}
</AnimatePresence>

        </motion.div>
      </Marker>

      {/* Popup - Keep your existing popup JSX here */}
       {/* ✅ COMPLETE POPUP CARD */}
      <AnimatePresence mode="wait">
       {showPopup && (
  <Popup
    longitude={numLng}
    latitude={numLat}
    anchor={popupPosition?.adjustedAnchor || 'bottom'}
    onClose={handleClosePopup}
    closeButton={false}
    closeOnClick={false}
    maxWidth="none"
    className="venue-popup enhanced-auto-fit-popup"
    offsetTop={-60}
  >
    {console.log('🎉 Popup rendering for:', venue.name)}
            <motion.div
              initial={{ opacity: 0, scale: 0.7, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.7, y: 30 }}
              transition={{ 
                type: "spring",
                stiffness: 260,
                damping: 20,
                mass: 0.9
              }}
              onClick={handlePopupClick}
              style={{
                padding: 0,
                width: `${popupDimensions.width}px`,
                maxWidth: `${popupDimensions.width}px`,
                maxHeight: `${popupDimensions.height}px`,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                borderRadius: popupDimensions.borderRadius,
                overflow: 'hidden',
                boxShadow: '0 32px 100px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1)',
                background: 'white',
                willChange: 'transform, opacity',
                backdropFilter: 'blur(24px)',
                position: 'relative',
                zIndex: 9999
              }}
            >
              {/* Content area */}
              <div 
                onClick={handlePopupContentClick}
                style={{ 
                  padding: popupDimensions.padding,
                  position: 'relative',
                  background: 'white',
                  maxHeight: `${popupDimensions.height}px`,
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}
              >
                {/* Close button - Moved to content area */}
              <motion.button
                  onClick={handleCloseButtonClick}
                  whileHover={{ scale: 1.1, backgroundColor: '#f5933d' }}
                whileTap={{ scale: 0.95 }}
                style={{
                  position: 'absolute',
                    top: isMobile ? '8px' : '10px',
                    right: isMobile ? '8px' : '10px',
                    background: '#f5933d', // Orange background
                  border: 'none',
                  borderRadius: '50%',
                    width: isMobile ? '32px' : '36px', // Slightly larger for better visibility
                    height: isMobile ? '32px' : '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 1,
                    boxShadow: '0 4px 12px rgba(217, 100, 31, 0.3)', // Orange shadow
                  backdropFilter: 'blur(16px)',
                  transition: 'all 0.2s ease'
                }}
              >
                <XMarkIcon style={{ 
                    height: isMobile ? '20px' : '22px', // Increased icon size
                    width: isMobile ? '20px' : '22px', 
                    color: 'white' // White X icon
                }} />
              </motion.button>

                {/* Verified badge */}
                {false && /* field removed - no Neon equivalent yet for Wifi_bage */ (
                  <motion.div 
                    initial={{ scale: 0, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.6, type: "spring", stiffness: 300 }}
                    style={{
                      position: 'absolute',
                      top: isMobile ? '4px' : isTablet ? '6px' : '8px', 
                      right: isMobile ? '40px' : '50px', // Adjusted to avoid close button
                      width: isMobile ? '30px' : isTablet ? '35px' : '40px',
                      height: isMobile ? '25px' : isTablet ? '30px' : '35px',
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      zIndex: 999,
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                    }} 
                  />
                )}

                <div style={{ marginBottom: isMobile ? '8px' : isTablet ? '10px' : '12px' }}> {/* Increased margin */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'space-between', 
                    marginBottom: isMobile ? '8px' : '10px', // Increased margin
                    gap: '6px' // Increased gap
                  }}>
                    <motion.h3 
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      style={{
                        fontWeight: 'bold',
                        color: '#111827',
                        fontSize: isMobile ? '17px' : isTablet ? '19px' : '21px', // Increased font size
                        lineHeight: 1.2, // Increased line height
                        margin: 0,
                        flex: 1,
                        paddingRight: '40px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {venue.name || venue.vendorName || venue.name || t('map.unnamedVenue')}
                    </motion.h3>

                    {false && /* field removed - no Neon equivalent yet for HL_Price_Level */ (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2 }}
                        style={{ display: 'flex', flexShrink: 0 }}
                      >
                        {[...Array(4)].map((_, i) => (
                          <span
                            key={i}
                            style={{
                              fontSize: isMobile ? '10px' : isTablet ? '12px' : '14px',
                              color: i < venue.HL_Price_Level ? '#f59e0b' : '#d1d5db',
                              fontWeight: 'bold'
                            }}
                          >
                            $
                          </span>
                        ))}
                      </motion.div>
                    )}
                  </div>

                  {/* Rating and Location in same row */}
                  <motion.div 
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      gap: isMobile ? '12px' : '16px', // Increased gap
                      marginBottom: isMobile ? '10px' : '12px', // Increased margin
                      flexWrap: 'nowrap'
                    }}
                  >
                  {/* Last update timestamp - Real-time indicator */}
                  {venue.isStreetVendor && venue.lastLocationUpdate && (
                    <div style={{
                      fontSize: '10px',
                      color: '#10b981',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        style={{
                          width: '6px',
                          height: '6px',
                          backgroundColor: '#10b981',
                          borderRadius: '50%'
                        }}
                      />
                      <span>{t('map.liveUpdatedAgo', { seconds: Math.floor((new Date() - new Date(venue.lastLocationUpdate)) / 1000) })}</span>
                    </div>
                  )}
                    {/* Rating */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', flexShrink: 0 }}> {/* Increased gap */}
                      <StarIcon style={{ 
                        height: isMobile ? '18px' : isTablet ? '20px' : '22px', // Increased size
                        width: isMobile ? '18px' : isTablet ? '20px' : '22px', 
                        color: '#fbbf24', 
                        fill: '#fbbf24' 
                      }} />
                      <span style={{ 
                        fontSize: isMobile ? '15px' : isTablet ? '16px' : '18px', // Increased font size
                        fontWeight: '600', 
                        color: '#374151'
                      }}>
                        {venue.Rating}
                      </span>
                    </div>

                    {/* Location */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px', // Increased font size
                      color: '#6b7280', 
                      flex: 1,
                      minWidth: 0
                    }}>
                    <MapPinIcon style={{ 
                        height: isMobile ? '16px' : isTablet ? '18px' : '20px', // Increased size
                        width: isMobile ? '16px' : isTablet ? '18px' : '20px', 
                        marginRight: isMobile ? '4px' : '6px', // Increased margin
                        flexShrink: 0
                    }} />
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {venue.district || venue.city || venue.city || t('map.unknownLocation')}
                    </span>
                    </div>
                  </motion.div>

                  {/* Enhanced Feature badges with all attributes */}
                  <motion.div 
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    style={{
                      display: 'flex',
                      gap: isMobile ? '4px' : '6px', // Increased gap
                      flexWrap: 'wrap',
                      marginBottom: isMobile ? '10px' : '12px' // Increased margin
                    }}
                  >
                    {/* Noise Level Badge */}
                    {venue.Noise_Level && false && (
                      <span style={{
                        backgroundColor: venue.Noise_Level.toLowerCase() === 'quiet' ? '#f0fdf4' : 
                                        venue.Noise_Level.toLowerCase() === 'moderate' ? '#fef3c7' : '#fef2f2',
                        color: venue.Noise_Level.toLowerCase() === 'quiet' ? '#166534' : 
                               venue.Noise_Level.toLowerCase() === 'moderate' ? '#92400e' : '#dc2626',
                        fontSize: isMobile ? '11px' : '12px', // Increased font size
                        fontWeight: '600',
                        padding: isMobile ? '4px 6px' : '5px 8px', // Increased padding
                        borderRadius: isMobile ? '4px' : '5px', // Increased border radius
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px' // Increased gap
                      }}>
                        <span style={{ fontSize: isMobile ? '8px' : '9px' }}>
                          {venue.Noise_Level.toLowerCase() === 'quiet' ? '🔇' : 
                           venue.Noise_Level.toLowerCase() === 'moderate' ? '🔉' : '🔊'}
                        </span>
                        {venue.Noise_Level}
                      </span>
                    )}

                    {/* WiFi Badge */}
                    {showWifiBadges && venue.Pub_Wifi === 1 && (
                    <span style={{
                      backgroundColor: '#dbeafe',
                      color: '#1d4ed8',
                      fontSize: isMobile ? '9px' : '10px',
                      fontWeight: '600',
                      padding: isMobile ? '2px 4px' : '3px 6px',
                      borderRadius: isMobile ? '3px' : '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <WifiIcon style={{ 
                        height: isMobile ? '10px' : '12px',
                        width: isMobile ? '10px' : '12px'
                      }} /> 
                      {venue.DL_SPeed_MBPS && (
                        <span style={{ fontSize: isMobile ? '10px' : '9px' }}>
                          ↓{venue.DL_SPeed_MBPS}
                        </span>
                      )}
                      {venue.UL_SPeed_MBPS && (
                        <span style={{ fontSize: isMobile ? '10px' : '9px' }}>
                          ↑{venue.UL_SPeed_MBPS}
                        </span>
                      )}
                    </span>
                    )}

                    {/* Budget Badge */}
                    {showBudgetBadges && venue.Budget_Friendly && (
                    <span style={{
                      backgroundColor: (() => {
                        const budgetString = venue.Budget_Friendly;
                        if (budgetString === '$$$$') return 'rgba(239, 68, 68, 0.1)';
                        if (budgetString === '$$$') return 'rgba(251, 146, 60, 0.1)';
                        if (budgetString === '$$') return 'rgba(34, 197, 94, 0.1)';
                        if (budgetString === '$') return 'rgba(59, 130, 246, 0.1)';
                        return 'rgba(107, 114, 128, 0.1)';
                      })(),
                      color: (() => {
                        const budgetString = venue.Budget_Friendly;
                        if (budgetString === '$$$$') return '#dc2626';
                        if (budgetString === '$$$') return '#ea580c';
                        if (budgetString === '$$') return '#16a34a';
                        if (budgetString === '$') return '#2563eb';
                        return '#4b5563';
                      })(),
                      fontSize: isMobile ? '9px' : '10px',
                      fontWeight: '600',
                      padding: isMobile ? '2px 4px' : '3px 6px',
                      borderRadius: isMobile ? '3px' : '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <span style={{ fontSize: isMobile ? '10px' : '12px' }}>💰</span>
                      <span>{venue.Budget_Friendly}</span>
                    </span>
                    )}

                    {/* Rating Badge */}
                    {showRatingsBadges && venue.Rating && (
                      <span style={{
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        color: '#b45309',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        <span style={{ fontSize: isMobile ? '10px' : '12px' }}>⭐</span>
                        <span>{venue.Rating}</span>
                      </span>
                    )}

                    {/* Charging Ports - Updated with plug icon */}
                    {venue.Charging_Ports > 0 && (
                      <span style={{
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        🔌 {venue.Charging_Ports}
                      </span>
                    )}

                    {/* Top Rated */}
                    {venue.Rating && venue.Rating >= 4.5 && (
                      <span style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        ⭐
                      </span>
                    )}

                    {/* Budget Friendly - Fixed to use Budget_Friendly from API */}
                    {venue.Budget_Friendly && (
                      <span style={{
                        backgroundColor: '#f0fdf4',
                        color: '#166534',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        {(() => {
                          // Parse the Budget_Friendly value to get number of $ signs
                          const budgetValue = venue.Budget_Friendly;
                          let dollarCount = 1; // Default to 1 if parsing fails
                          
                          if (typeof budgetValue === 'string') {
                            // Extract number from string like "$" or "$$" or "$$$"
                            const match = budgetValue.match(/\$/g);
                            dollarCount = match ? match.length : 1;
                          } else if (typeof budgetValue === 'number') {
                            dollarCount = Math.max(1, Math.min(4, budgetValue));
                          }
                          
                          return (
                            <>
                              {[...Array(4)].map((_, i) => (
                                <span
                                  key={i}
                                  style={{
                                    fontSize: isMobile ? '6px' : '7px',
                                    color: i < dollarCount ? '#166534' : '#9ca3af',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  $
                                </span>
                              ))}
                            </>
                          );
                        })()}
                      </span>
                    )}

                    {/* Pet Friendly - Enhanced text */}
                    {venue.Pet_Policy && venue.Pet_Policy.toLowerCase().includes('allowed') && (
                      <span style={{
                        backgroundColor: '#f0fdf4',
                        color: '#166534',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        🐕
                      </span>
                    )}

                    {/* Offers */}
                    {venue.Offers && (
                      <span style={{
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        🎉 {t('zones.titleOffers')}
                      </span>
                    )}

                    {/* Day Pass Price */}
                    {venue.DayPassPrice && (
                      <span style={{
                        backgroundColor: '#e0e7ff',
                        color: '#3730a3',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        🎫 {t('map.dayPassBadge')}
                      </span>
                    )}

                    {/* Entrance Fee */}
                    {venue.Entrance_Fee && venue.Entrance_Fee > 0 && (
                      <span style={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        🚪
                      </span>
                    )}

                    {/* Payment Types */}
                    {false && /* field removed - no Neon equivalent yet for Payment_types */ (
                      <span style={{
                        backgroundColor: '#f0fdf4',
                        color: '#166534',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        💳 Cards
                      </span>
                    )}

                    {/* AC Fan */}
                    {false && /* field removed - no Neon equivalent yet for HL_zoho_AC_Fan */ (
                      <span style={{
                        backgroundColor: '#f0f9ff',
                        color: '#0ea5e9',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        ❄️ AC
                      </span>
                    )}

                    {/* Sunset Filter */}
                    {venue.Sunset_Filter && (
                      <span style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        fontSize: isMobile ? '9px' : '10px',
                        fontWeight: '600',
                        padding: isMobile ? '2px 4px' : '3px 6px',
                        borderRadius: isMobile ? '3px' : '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        🌅
                      </span>
                    )}
                  </motion.div>
                </div>

                {/* Mosque info - Collapsible */}
                {nearestMosque && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    style={{
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: isMobile ? '6px' : '8px',
                      padding: isMobile ? '6px' : '8px',
                      marginBottom: isMobile ? '6px' : '8px',
                      position: 'relative',
                      boxShadow: '0 2px 8px rgba(14, 165, 233, 0.15)'
                    }}
                  >
                    {/* Mosque header - Clickable */}
                    <motion.div 
                      onClick={() => setShowMosqueDetails(!showMosqueDetails)}
                      whileHover={{ backgroundColor: 'rgba(14, 165, 233, 0.1)' }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: isMobile ? '2px' : '4px',
                        borderRadius: isMobile ? '4px' : '6px',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px' }}>
                        <span style={{ fontSize: isMobile ? '12px' : '14px' }}>🕌</span>
                        <div>
                          <div style={{
                            fontSize: isMobile ? '9px' : '10px', // Even smaller
                            fontWeight: '700',
                            color: '#0ea5e9',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {t('map.nearestMosque')}
                          </div>
                          <div style={{
                            fontSize: isMobile ? '8px' : '9px', // Even smaller
                            color: '#6b7280',
                            fontWeight: '500'
                          }}>
                            {nearestMosque.name?.substring(0, 20) || t('map.mosqueFallback')}
                            {nearestMosque.name?.length > 20 && '...'}
                          </div>
                        </div>
                      </div>
                      <motion.div
                        animate={{ rotate: showMosqueDetails ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        style={{
                          fontSize: isMobile ? '10px' : '12px',
                          color: '#0ea5e9'
                        }}
                      >
                        ▼
                      </motion.div>
                    </motion.div>

                    {/* Collapsible mosque details */}
                    <AnimatePresence>
                      {showMosqueDetails && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          style={{ overflow: 'hidden' }}
                        >
                          <div style={{
                            paddingTop: isMobile ? '4px' : '6px',
                            borderTop: '1px solid rgba(14, 165, 233, 0.2)'
                          }}>
                            {/* Distance display */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              fontSize: isMobile ? '9px' : '10px', // Even smaller
                              color: '#6b7280',
                              marginBottom: isMobile ? '4px' : '6px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '3px' : '4px' }}>
                                <MapPinIcon style={{ 
                                  height: isMobile ? '12px' : '14px', // Increased size
                                  width: isMobile ? '12px' : '14px' 
                                }} />
                                <span>
                                  {nearestMosque.district || nearestMosque.city}
                                </span>
                              </div>
                              <div style={{
                                display: 'flex',
                                gap: isMobile ? '4px' : '6px'
                              }}>
                                <div style={{
                                  backgroundColor: '#0ea5e9',
                                  color: 'white',
                                  padding: isMobile ? '3px 6px' : '4px 8px', // Even smaller
                                  borderRadius: isMobile ? '6px' : '8px',
                                  fontSize: isMobile ? '9px' : '10px', // Even smaller
                                  fontWeight: '700',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: isMobile ? '3px' : '4px',
                                  boxShadow: '0 2px 6px rgba(14, 165, 233, 0.3)'
                                }}>
                                  🚶 {mosqueRoute?.distance} km
                                </div>
                                <div style={{
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  padding: isMobile ? '3px 6px' : '4px 8px', // Even smaller
                                  borderRadius: isMobile ? '6px' : '8px',
                                  fontSize: isMobile ? '9px' : '10px', // Even smaller
                                  fontWeight: '700',
                                  boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
                                }}>
                                  ⏱️ {mosqueRoute?.duration} min
                                </div>
                              </div>
                            </div>

                            {/* Direction info */}
                            {mosqueDirection && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                                padding: isMobile ? '4px 6px' : '6px 8px', // Even smaller
                                borderRadius: isMobile ? '4px' : '6px',
                                border: '1px solid rgba(14, 165, 233, 0.2)',
                                marginBottom: null /* field removed - no Neon equivalent yet for HL_Opening_Hours_Text */ ? isMobile ? '4px' : '6px' : '0'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: isMobile ? '3px' : '4px',
                                  fontSize: isMobile ? '8px' : '9px', // Even smaller
                                  color: '#0ea5e9',
                                  fontWeight: '600'
                                }}>
                                  <ArrowTopRightOnSquareIcon style={{ 
                                    height: isMobile ? '12px' : '14px', // Increased size
                                    width: isMobile ? '12px' : '14px' 
                                  }} />
                                  <span>{t('map.routeBearing', { bearing: Math.round(mosqueDirection.bearing), arrow: mosqueDirection.arrow })}</span>
                                </div>
                                <div style={{
                                  fontSize: isMobile ? '9px' : '10px', // Even smaller
                                  color: '#059669',
                                  fontWeight: '600',
                                  backgroundColor: 'rgba(5, 150, 105, 0.15)',
                                  padding: '1px 4px',
                                  borderRadius: '3px'
                                }}>
                                  {showPath ? `✅ ${t('map.pathShown')}` : `👆 ${t('map.pathTap')}`}
                                </div>
                              </div>
                            )}

                            {null /* field removed - no Neon equivalent yet for HL_Opening_Hours_Text */ && (
                              <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.6 }}
                                style={{
                                  fontSize: isMobile ? '9px' : '10px', // Even smaller
                                  color: '#059669',
                                  backgroundColor: 'rgba(5, 150, 105, 0.15)',
                                  padding: isMobile ? '3px 4px' : '4px 6px',
                                  borderRadius: isMobile ? '3px' : '4px',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  border: '1px solid rgba(5, 150, 105, 0.25)'
                                }}
                              >
                                📍 {null /* field removed - no Neon equivalent yet for HL_Opening_Hours_Text */}
                              </motion.div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}

                {/* Action button */}
                <div style={{
                  borderTop: '1px solid #f3f4f6',
                  paddingTop: isMobile ? '10px' : isTablet ? '12px' : '14px', // Increased padding
                  display: 'flex',
                  gap: isMobile ? '6px' : '8px' // Increased gap
                }}>
                  <motion.button
                    onClick={handleViewDetails}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px 16px' : isTablet ? '14px 18px' : '16px 22px', // Increased padding
                      backgroundColor: '#f5933d',
                      color: 'white',
                      border: 'none',
                      borderRadius: isMobile ? '8px' : '10px', // Increased border radius
                      fontSize: isMobile ? '13px' : isTablet ? '14px' : '16px', // Increased font size
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.2s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {t('venue.viewDetails')}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </Popup>
        )}
      </AnimatePresence>

      <style jsx>{`
        .venue-popup.enhanced-auto-fit-popup .mapboxgl-popup-content {
          padding: 0 !important;
          border-radius: ${popupDimensions.borderRadius} !important;
          box-shadow: 0 32px 100px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1) !important;
          max-width: none !important;
          max-height: 90vh !important;
          overflow: hidden !important;
          border: none !important;
        }

        .venue-popup.enhanced-auto-fit-popup .mapboxgl-popup-tip {
          display: none !important;
        }


        .venue-popup.enhanced-auto-fit-popup .mapboxgl-popup-close-button {
          display: none !important;
        }

        .venue-popup.enhanced-auto-fit-popup div::-webkit-scrollbar {
          width: 4px;
        }

        .venue-popup.enhanced-auto-fit-popup div::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 2px;
        }

        .venue-popup.enhanced-auto-fit-popup div::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }

        .venue-popup.enhanced-auto-fit-popup div::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        @media (max-width: 480px) {
          .venue-popup.enhanced-auto-fit-popup .mapboxgl-popup-content {
            border-radius: 14px !important;
          }
        }

        @media (max-height: 600px) {
          .venue-popup.enhanced-auto-fit-popup .mapboxgl-popup-content {
            max-height: 85vh !important;
          }
        }
      `}</style>
    </>
  );
});

MapMarker.displayName = 'MapMarker';

export default MapMarker;